import { Container, Graphics, Text, TextStyle } from 'pixi.js';

export interface ProgressBarConfig {
  width: number;
  height: number;
  bgColor?: number;
  fillColor?: number;
  borderColor?: number;
  showText?: boolean;
  radius?: number;
}

export class ProgressBar extends Container {
  private bg: Graphics;
  private fill: Graphics;
  private textLabel: Text | null = null;
  private config: ProgressBarConfig;
  private _progress = 0;

  constructor(config: ProgressBarConfig) {
    super();
    this.config = config;

    const {
      width, height,
      bgColor = 0x333333,
      borderColor = 0x555555,
      radius = 4,
    } = config;

    // Background
    this.bg = new Graphics();
    this.bg.roundRect(0, 0, width, height, radius);
    this.bg.fill({ color: bgColor });
    this.bg.stroke({ color: borderColor, width: 1 });
    this.addChild(this.bg);

    // Fill
    this.fill = new Graphics();
    this.addChild(this.fill);

    // Text
    if (config.showText !== false) {
      const style = new TextStyle({
        fontSize: Math.max(10, height - 4),
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        fill: '#ffffff',
      });
      this.textLabel = new Text({ text: '0%', style });
      this.textLabel.anchor.set(0.5);
      this.textLabel.x = width / 2;
      this.textLabel.y = height / 2;
      this.addChild(this.textLabel);
    }

    this.setProgress(0);
  }

  setProgress(value: number) {
    this._progress = Math.max(0, Math.min(1, value));
    const { width, height, fillColor = 0x2d8a4e, radius = 4 } = this.config;

    this.fill.clear();
    if (this._progress > 0.01) {
      const fillWidth = Math.max(radius * 2, width * this._progress);
      this.fill.roundRect(0, 0, fillWidth, height, radius);
      this.fill.fill({ color: fillColor });
    }

    if (this.textLabel) {
      this.textLabel.text = `${Math.floor(this._progress * 100)}%`;
    }
  }

  setFillColor(color: number) {
    this.config.fillColor = color;
    this.setProgress(this._progress); // re-render with new color
  }

  get progress() {
    return this._progress;
  }
}
